import { useEffect, useState } from "react";
import {
  StyleSheet,
  Text,
  TextInput,
  View,
  type TextInputProps,
} from "react-native";

import {
  fonts,
  radii,
  useThemedStyles,
  type ThemeColors,
} from "~/styles/theme";
import {
  formatCurrencyAmountInput,
  getCurrencyLocale,
  getCurrencySeparators,
  getCurrencySymbol,
  parseCurrencyInput,
} from "~/utils/format-regional";

export function CurrencyInput({
  value,
  currency,
  locale = getCurrencyLocale(currency),
  onValueChange,
  accessibilityLabel = "Amount",
  ...props
}: Omit<TextInputProps, "value" | "onChangeText" | "keyboardType"> & {
  value: string;
  currency: string;
  locale?: string;
  onValueChange: (value: string) => void;
}) {
  const stylesheet = useThemedStyles(makeStyles);
  const [focused, setFocused] = useState(false);
  const [displayValue, setDisplayValue] = useState("");
  const { decimal } = getCurrencySeparators(locale);

  useEffect(() => {
    if (focused) return;
    setDisplayValue(formatCurrencyAmountInput(value, locale));
  }, [focused, locale, value]);

  const editableValue = (canonical: string) => canonical.replace(".", decimal);

  return (
    <View style={[stylesheet.container, focused ? stylesheet.focused : null]}>
      <Text style={stylesheet.symbol}>
        {getCurrencySymbol(currency, locale)}
      </Text>
      <TextInput
        {...props}
        accessibilityLabel={`${accessibilityLabel} in ${currency}`}
        keyboardType="decimal-pad"
        inputMode="decimal"
        style={stylesheet.input}
        value={displayValue}
        onFocus={(event) => {
          setFocused(true);
          setDisplayValue(editableValue(value));
          props.onFocus?.(event);
        }}
        onBlur={(event) => {
          setFocused(false);
          setDisplayValue(formatCurrencyAmountInput(value, locale));
          props.onBlur?.(event);
        }}
        onChangeText={(nextValue) => {
          const canonical = parseCurrencyInput(nextValue, currency, locale);
          onValueChange(canonical);
          setDisplayValue(editableValue(canonical));
        }}
      />
      <Text style={stylesheet.code}>{currency}</Text>
    </View>
  );
}

const makeStyles = (colors: ThemeColors) =>
  StyleSheet.create({
    container: {
      minHeight: 44,
      flexDirection: "row",
      alignItems: "center",
      borderWidth: 1,
      borderColor: colors.border,
      borderRadius: radii.m,
      backgroundColor: colors.background,
    },
    focused: {
      borderColor: colors.primary,
      shadowColor: colors.primary,
      shadowOffset: { width: 0, height: 0 },
      shadowOpacity: 0.18,
      shadowRadius: 0,
    },
    symbol: {
      minWidth: 34,
      paddingLeft: 10,
      color: colors.heading,
      fontFamily: fonts.semibold,
      fontSize: 14,
    },
    input: {
      flex: 1,
      minHeight: 44,
      padding: 10,
      color: colors.foreground,
      fontFamily: fonts.mono,
      outlineWidth: 0,
    },
    code: {
      paddingRight: 10,
      color: colors.mutedForeground,
      fontFamily: fonts.semibold,
      fontSize: 12,
    },
  });
